<template>
  <transition name="glad-pp-fade">
    <div v-if="show" class="glad-pp-overlay" @click.self="close" tabindex="-1">
      <div class="glad-pp" role="dialog" aria-modal="true" aria-labelledby="glad-pp-title">

        <button class="glad-pp__x" type="button" aria-label="Close" @click="close">&times;</button>

        <!-- Step indicator -->
        <ol class="glad-pp-steps" aria-hidden="true">
          <li :class="stepClass('browse')">1 · Select</li>
          <li :class="stepClass('payment')">2 · Payment</li>
          <li :class="stepClass('success')">3 · Done</li>
        </ol>

        <!-- STEP 1 — BROWSE / CART --------------------------------------- -->
        <section v-if="step === 'browse'" class="glad-pp-body">
          <p class="glad-pp-eyebrow">Pathway catalog</p>
          <h2 id="glad-pp-title" class="glad-pp-title">Purchase pathways</h2>
          <p class="glad-pp-lead">Select the pathways you’d like to purchase for full access.</p>

          <div v-if="!catalog.length" class="glad-pp-empty">No pathways are available to purchase right now.</div>

          <ul v-else class="glad-pp-catalog">
            <li
              v-for="p in catalog"
              :key="p.id"
              class="glad-pp-cat"
              :class="{ 'glad-pp-cat--in': inCart(p.id) }">
              <div class="glad-pp-cat__main">
                <p class="glad-pp-cat__title">{{ p.title }}</p>
                <p v-if="p.subtitle" class="glad-pp-cat__sub">{{ p.subtitle }}</p>
                <p class="glad-pp-cat__meta">{{ moduleCount(p) }} modules · full pathway access</p>
              </div>
              <div class="glad-pp-cat__right">
                <span class="glad-pp-cat__price">{{ money(priceOf(p)) }}</span>
                <button
                  type="button"
                  class="glad-pp-add"
                  :class="{ 'glad-pp-add--in': inCart(p.id) }"
                  @click="toggle(p)">
                  {{ inCart(p.id) ? '✓ Added' : 'Add' }}
                </button>
              </div>
            </li>
          </ul>

          <div class="glad-pp-cartbar">
            <span>{{ cart.length }} {{ cart.length === 1 ? 'pathway' : 'pathways' }} selected</span>
            <span class="glad-pp-cartbar__total">Total {{ money(cartTotal) }}</span>
          </div>

          <div class="glad-pp-actions">
            <button class="glad-pp-btn glad-pp-btn--ghost" type="button" @click="close">Cancel</button>
            <button class="glad-pp-btn" type="button" :disabled="!cart.length" @click="step = 'payment'">
              Continue to payment
            </button>
          </div>
        </section>

        <!-- STEP 2 — PAYMENT (Authorize.Net-style) ------------------------ -->
        <section v-else-if="step === 'payment'" class="glad-pp-body">
          <p class="glad-pp-eyebrow">Secure payment</p>
          <h2 id="glad-pp-title" class="glad-pp-title">Payment information</h2>

          <!-- In production this region is replaced by the Authorize.Net Accept
               Hosted iframe / AcceptUI button (apiLoginID + clientKey →
               opaqueData). This is the integration touchpoint; no card data is
               transmitted in this demo build. -->
          <div class="glad-pp-anet">
            <span class="glad-pp-anet__lock" aria-hidden="true">🔒</span>
            <span class="glad-pp-anet__txt">Payments secured by</span>
            <span class="glad-pp-anet__brand">Authorize<span class="glad-pp-anet__net">.Net</span></span>
          </div>

          <ul class="glad-pp-mini">
            <li v-for="p in cart" :key="p.id">
              <span>{{ p.title }}</span>
              <span>{{ money(priceOf(p)) }}</span>
            </li>
          </ul>

          <form class="glad-pp-form" @submit.prevent="pay">
            <label class="glad-pp-field glad-pp-field--full">
              <span class="glad-pp-field__label">Name on card</span>
              <input v-model.trim="card.name" type="text" autocomplete="cc-name" placeholder="Jane Educator" required />
            </label>

            <label class="glad-pp-field glad-pp-field--full">
              <span class="glad-pp-field__label">Card number</span>
              <input :value="card.number" @input="onCardNumber" type="text" inputmode="numeric" autocomplete="cc-number" placeholder="4111 1111 1111 1111" maxlength="23" required />
            </label>

            <label class="glad-pp-field">
              <span class="glad-pp-field__label">Expiration</span>
              <input :value="card.exp" @input="onExp" type="text" inputmode="numeric" autocomplete="cc-exp" placeholder="MM / YY" maxlength="7" required />
            </label>

            <label class="glad-pp-field">
              <span class="glad-pp-field__label">CVV</span>
              <input v-model="card.cvv" type="text" inputmode="numeric" autocomplete="cc-csc" placeholder="123" maxlength="4" required />
            </label>

            <label class="glad-pp-field">
              <span class="glad-pp-field__label">Billing ZIP</span>
              <input v-model.trim="card.zip" type="text" inputmode="numeric" autocomplete="postal-code" placeholder="92701" maxlength="10" required />
            </label>

            <p v-if="formError" class="glad-pp-error">{{ formError }}</p>

            <div class="glad-pp-actions glad-pp-actions--span">
              <button class="glad-pp-btn glad-pp-btn--ghost" type="button" @click="step = 'browse'" :disabled="processing">Back</button>
              <button class="glad-pp-btn" type="submit" :disabled="processing">
                <span v-if="processing" class="glad-pp-spinner"></span>
                {{ processing ? 'Contacting Authorize.Net…' : 'Pay ' + money(cartTotal) }}
              </button>
            </div>
          </form>

          <p class="glad-pp-demo-note">Demo environment — no live charge is made.</p>
        </section>

        <!-- STEP 3 — SUCCESS --------------------------------------------- -->
        <section v-else class="glad-pp-body glad-pp-body--center">
          <div class="glad-pp-check" aria-hidden="true">✓</div>
          <h2 id="glad-pp-title" class="glad-pp-title">Payment approved</h2>
          <p class="glad-pp-success-sub">
            {{ purchased.length }} {{ purchased.length === 1 ? 'pathway is' : 'pathways are' }} now unlocked.
          </p>

          <ul class="glad-pp-mini glad-pp-mini--receipt">
            <li v-for="p in purchased" :key="p.id">
              <span>{{ p.title }}</span>
              <span>{{ money(priceOf(p)) }}</span>
            </li>
          </ul>

          <dl class="glad-pp-receipt">
            <div><dt>Amount</dt><dd>{{ money(purchasedTotal) }}</dd></div>
            <div><dt>Transaction ID</dt><dd>{{ transactionId }}</dd></div>
            <div><dt>Processor</dt><dd>Authorize.Net (demo)</dd></div>
          </dl>

          <div class="glad-pp-actions glad-pp-actions--center">
            <button class="glad-pp-btn" type="button" @click="finish">Go to my pathways</button>
          </div>
        </section>

      </div>
    </div>
  </transition>
</template>

<script>
const BASE_PRICE = 99;
const PER_MODULE = 25;

export default {
  name: 'PathwayPurchaseModal',
  props: {
    show: { type: Boolean, default: false },
    // Full pathway list from the dashboard; the modal filters to purchasable ones.
    pathways: { type: Array, default: () => [] },
  },
  data() {
    return {
      step: 'browse',
      cart: [],          // selected pathway objects
      purchased: [],     // snapshot of what was bought (for the receipt)
      processing: false,
      formError: '',
      transactionId: '',
      card: { name: '', number: '', exp: '', cvv: '', zip: '' },
    };
  },
  computed: {
    // Only published pathways are purchasable.
    catalog() {
      return (this.pathways || []).filter(p => (p.status || 'published') === 'published');
    },
    cartTotal() {
      return this.cart.reduce((sum, p) => sum + this.priceOf(p), 0);
    },
    purchasedTotal() {
      return this.purchased.reduce((sum, p) => sum + this.priceOf(p), 0);
    },
  },
  watch: {
    show(open) {
      if (open) {
        this.step = 'browse';
        this.cart = [];
        this.purchased = [];
        this.processing = false;
        this.formError = '';
        this.transactionId = '';
        this.card = { name: '', number: '', exp: '', cvv: '', zip: '' };
      }
    },
  },
  methods: {
    stepClass(name) {
      const order = ['browse', 'payment', 'success'];
      const here = order.indexOf(this.step);
      const idx = order.indexOf(name);
      return {
        'glad-pp-steps__step--active': idx === here,
        'glad-pp-steps__step--done': idx < here,
      };
    },
    moduleCount(p) {
      if (typeof p.module_count === 'number') return p.module_count;
      return Array.isArray(p.modules) ? p.modules.length : 0;
    },
    priceOf(p) {
      return BASE_PRICE + PER_MODULE * this.moduleCount(p);
    },
    money(n) {
      return '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    inCart(id) {
      return this.cart.some(p => p.id === id);
    },
    toggle(p) {
      const idx = this.cart.findIndex(x => x.id === p.id);
      if (idx === -1) this.cart.push(p);
      else this.cart.splice(idx, 1);
    },
    onCardNumber(e) {
      const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 16);
      this.card.number = digits.replace(/(.{4})/g, '$1 ').trim();
      e.target.value = this.card.number;
    },
    onExp(e) {
      let digits = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
      if (digits.length >= 3) digits = digits.slice(0, 2) + ' / ' + digits.slice(2);
      this.card.exp = digits;
      e.target.value = this.card.exp;
    },
    validate() {
      const digits = this.card.number.replace(/\D/g, '');
      if (!this.card.name) return 'Enter the name on the card.';
      if (digits.length < 13) return 'Enter a valid card number.';
      if (!/^\d{2}\s*\/\s*\d{2}$/.test(this.card.exp)) return 'Enter the expiration as MM / YY.';
      if (this.card.cvv.replace(/\D/g, '').length < 3) return 'Enter the card security code.';
      if (!this.card.zip) return 'Enter the billing ZIP.';
      return '';
    },
    pay() {
      const err = this.validate();
      if (err) { this.formError = err; return; }
      this.formError = '';
      this.processing = true;

      // Simulated Authorize.Net round-trip. A real integration would POST the
      // tokenized opaqueData to the backend, which calls the Authorize.Net
      // createTransactionRequest API and returns the transaction result.
      setTimeout(() => {
        this.processing = false;
        this.purchased = this.cart.slice();
        this.transactionId = this.mockTransactionId();
        this.step = 'success';
        this.$emit('paid', {
          pathwayIds: this.purchased.map(p => p.id),
          amount: this.purchasedTotal,
          transactionId: this.transactionId,
        });
      }, 1500);
    },
    mockTransactionId() {
      const n = Math.floor(Math.random() * 1e10).toString().padStart(10, '0');
      return '60' + n;
    },
    finish() {
      this.$emit('completed', { pathwayIds: this.purchased.map(p => p.id) });
      this.close();
    },
    close() {
      this.$emit('update:show', false);
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.glad-pp-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 17, 21, 0.55);
  font-family: var(--glad-font-sans);
}

.glad-pp {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 92vh;
  overflow-y: auto;
  background: var(--glad-surface, #fff);
  border-radius: var(--glad-radius-lg, 14px);
  box-shadow: 0 24px 60px rgba(15, 17, 21, 0.28);
}
.glad-pp__x {
  position: absolute;
  top: 0.6rem;
  right: 0.85rem;
  border: 0;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  color: var(--glad-text-muted);
  cursor: pointer;
  z-index: 2;
  &:hover { color: var(--glad-ink); }
}

/* Steps -------------------------------------------------------------------- */
.glad-pp-steps {
  display: flex;
  gap: 0.4rem;
  list-style: none;
  margin: 0;
  padding: 1rem 1.5rem 0;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--glad-text-muted);
  li { white-space: nowrap; }
  li:not(:last-child)::after { content: '›'; margin-left: 0.4rem; color: var(--glad-line-strong); }
}
.glad-pp-steps__step--active { color: var(--glad-accent); }
.glad-pp-steps__step--done { color: var(--glad-ink); }

/* Body --------------------------------------------------------------------- */
.glad-pp-body { padding: 1rem 1.5rem 1.6rem; }
.glad-pp-body--center { text-align: center; }

.glad-pp-eyebrow {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  margin: 0 0 0.3rem;
}
.glad-pp-title {
  font-family: var(--glad-font-serif, Georgia, serif);
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.4rem;
  line-height: 1.2;
}
.glad-pp-lead { font-size: 0.88rem; color: var(--glad-text-soft); margin: 0 0 1rem; }
.glad-pp-empty { font-size: 0.88rem; color: var(--glad-text-soft); padding: 1.5rem 0; text-align: center; }

/* Catalog ------------------------------------------------------------------ */
.glad-pp-catalog { list-style: none; margin: 0 0 1rem; padding: 0; }
.glad-pp-cat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0;
  border-top: 1px solid var(--glad-line);
  &:last-child { border-bottom: 1px solid var(--glad-line); }
}
.glad-pp-cat--in { background: rgba(180, 83, 9, 0.04); }
.glad-pp-cat__main { flex: 1; min-width: 0; }
.glad-pp-cat__title { font-weight: 700; color: var(--glad-ink); margin: 0; }
.glad-pp-cat__sub {
  font-size: 0.82rem; color: var(--glad-text-soft); margin: 0.15rem 0 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.glad-pp-cat__meta { font-size: 0.74rem; color: var(--glad-text-muted); margin: 0.3rem 0 0; }
.glad-pp-cat__right { display: flex; flex-direction: column; align-items: flex-end; gap: 0.4rem; white-space: nowrap; }
.glad-pp-cat__price { font-weight: 700; color: var(--glad-ink); font-variant-numeric: tabular-nums; }

.glad-pp-add {
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--glad-ink);
  background: transparent;
  border: 1.5px solid var(--glad-line-strong);
  border-radius: var(--glad-radius-sm, 8px);
  padding: 0.32rem 0.85rem;
  cursor: pointer;
  &:hover { background: var(--glad-paper-2); }
  &--in { color: #fff; background: var(--glad-accent); border-color: var(--glad-accent); }
}

.glad-pp-cartbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 0.85rem;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius-sm, 8px);
  font-size: 0.85rem;
  color: var(--glad-text-soft);
}
.glad-pp-cartbar__total { font-weight: 700; color: var(--glad-ink); font-variant-numeric: tabular-nums; }

/* Mini line-item list (payment + receipt) ---------------------------------- */
.glad-pp-mini {
  list-style: none; margin: 0 0 1.1rem; padding: 0;
  li {
    display: flex; justify-content: space-between; gap: 1rem;
    font-size: 0.84rem; color: var(--glad-text-soft);
    padding: 0.3rem 0;
  }
  li span:last-child { font-variant-numeric: tabular-nums; color: var(--glad-ink); }
}
.glad-pp-mini--receipt { text-align: left; }

/* Authorize.Net strip ------------------------------------------------------ */
.glad-pp-anet {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 0.8rem;
  background: var(--glad-paper-2);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm, 8px);
  margin-bottom: 1.1rem;
  font-size: 0.82rem;
  color: var(--glad-text-soft);
}
.glad-pp-anet__brand { font-weight: 800; color: #1a1a2e; letter-spacing: -0.01em; margin-left: auto; }
.glad-pp-anet__net { color: #e2231a; }

/* Form --------------------------------------------------------------------- */
.glad-pp-form { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.glad-pp-field { display: flex; flex-direction: column; gap: 0.3rem; }
.glad-pp-field--full { grid-column: 1 / -1; }
.glad-pp-field__label { font-size: 0.74rem; font-weight: 600; color: var(--glad-text-soft); }
.glad-pp-field input {
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  color: var(--glad-ink);
  background: var(--glad-surface, #fff);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm, 8px);
  padding: 0.55rem 0.65rem;
  outline: none;
  &:focus { border-color: var(--glad-accent); }
}
.glad-pp-error { grid-column: 1 / -1; margin: 0; font-size: 0.82rem; color: #b3261e; }

/* Actions ------------------------------------------------------------------ */
.glad-pp-actions { display: flex; justify-content: flex-end; gap: 0.6rem; margin-top: 1.4rem; }
.glad-pp-actions--span { grid-column: 1 / -1; }
.glad-pp-actions--center { justify-content: center; }

.glad-pp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  background: var(--glad-ink);
  border: 1px solid var(--glad-ink);
  border-radius: var(--glad-radius-sm, 8px);
  padding: 0.6rem 1.1rem;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.92; }
  &:disabled { opacity: 0.5; cursor: default; }

  &--ghost { color: var(--glad-ink); background: transparent; border-color: var(--glad-line-strong); }
}

.glad-pp-demo-note { margin: 0.9rem 0 0; text-align: center; font-size: 0.72rem; color: var(--glad-text-muted); }

/* Success ------------------------------------------------------------------ */
.glad-pp-check {
  width: 56px; height: 56px; margin: 0.5rem auto 0.9rem;
  border-radius: 50%;
  background: rgba(180, 83, 9, 0.12);
  color: var(--glad-accent);
  font-size: 1.7rem; line-height: 56px; font-weight: 700;
}
.glad-pp-success-sub { font-size: 0.9rem; color: var(--glad-text-soft); margin: 0 0 1.1rem; }
.glad-pp-receipt {
  text-align: left;
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm, 8px);
  padding: 0.4rem 0.85rem;
  margin: 0 0 1.4rem;
  div { display: flex; justify-content: space-between; padding: 0.4rem 0; font-size: 0.85rem; }
  div + div { border-top: 1px solid var(--glad-line); }
  dt { color: var(--glad-text-soft); margin: 0; }
  dd { color: var(--glad-ink); font-weight: 600; margin: 0; font-variant-numeric: tabular-nums; }
}

/* Spinner + transition ----------------------------------------------------- */
.glad-pp-spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: glad-pp-spin 0.7s linear infinite;
}
@keyframes glad-pp-spin { to { transform: rotate(360deg); } }

.glad-pp-fade-enter-active, .glad-pp-fade-leave-active { transition: opacity 0.18s ease; }
.glad-pp-fade-enter, .glad-pp-fade-leave-to { opacity: 0; }
</style>
